import React from 'react';
import UI from '../ui';
import Utils from '../utils';
import { useChargeFormatter } from './hooks/useChargeFormatter';

const { WrappedButton, WrappedButtonType, Popover, Spinner } = UI;

const { Navigation, Session } = Utils;

type TotalChargeSummaryProps = {
	purchaseAmount: number;
	surchargeAmount?: number;
	totalAmount?: number;
	className?: string;
	isLoading?: boolean;
};

export const TotalChargeSummary = ({
	purchaseAmount = 0,
	surchargeAmount = 0,
	totalAmount = 0,
	className = '',
	isLoading = false,
}: TotalChargeSummaryProps) => {
	const [isRedirecting, setIsRedirecting] = React.useState(false);

	const { nsaSurchargePercentage } = Session.getDynamicSettings();

	const {
		formattedTotalCharge,
		formattedPurchaseAmount,
		formattedSurchargeAmount,
	} = useChargeFormatter(purchaseAmount, surchargeAmount, totalAmount);

	const navigateToPaymentSettings = (): void => {
		setIsRedirecting(true);
		Navigation.navigateTo('paymentSettings');
	};

	return (
		<ul className={`total-charge-summary ${className}`}>
			<li className="charge-row">
				<span>Funds</span>
				<span>{formattedPurchaseAmount}</span>
			</li>
			<li className="charge-row">
				<div className="fee-info-container">
					<span>Credit Card Processing Fee ({nsaSurchargePercentage}%)</span>
					<Popover
						trigger={
							<i
								className="help"
								role="img"
								tabIndex={0}
								aria-label="NSA Surcharges Info"
								data-qa="surcharge-info-icon-trigger"
							/>
						}
						placement="top"
						showCloseIcon={true}
						message={`A ${nsaSurchargePercentage}% fee applies when adding funds to your balance, including auto-funding, using a credit card. Switch to Pay by Bank to avoid the fee.`}
						actionsRight={
							<WrappedButton
								label={isRedirecting ? 'Redirecting...' : 'Go to settings'}
								buttonType={WrappedButtonType.Secondary}
								data-qa="surcharge-info-go-to-settings-button"
								onClick={navigateToPaymentSettings}
								disabled={isRedirecting}
							/>
						}
					/>
				</div>
				<span>{formattedSurchargeAmount}</span>
			</li>
			<li className="charge-row total-charge-row">
				<span>Total Charge</span>
				<span>{formattedTotalCharge}</span>
			</li>

			{isLoading && <Spinner />}
		</ul>
	);
};
